/*
 *
 *  * Copyright (c) Funtip GbR 2018.
 *  * Simon Oldeboershuis, Perry Prust
 *
 */

import { all, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { Action } from 'typescript-fsa';
import { bettingPath } from '.';
import { loadWithoutPropsTournamentRoundForwarder } from '../../reactUtils';
import { Bet, GameBet, GameBetInfo } from '../../services/betting';
import { fetchWrapperFunc, fetchWrapperFuncNoLoading } from '../../utils';
import { isLoggedInSelector } from '../System/selectors';
import { getBettingApi2, getSystemConfig, getTournamentApi2 } from '../System/systemConfig';
import { tournamentSelectionChangeTournamentRound } from '../TournamentSelectionComponent/actions';
import {
    BetQuoteRequest,
    bettingSetBetQuote,
    bettingSetBetQuoteGame,
    bettingSetGameInfo,
    bettingSetLoading,
    bettingSetTournament,
    bettingSetTournamentRound,
    bettingSetTrId,
    bettingShowBetQuote,
    BETTING_LOAD,
    BETTING_LOAD_BET_QUOTE,
    BETTING_LOAD_GAME_INFO,
    BETTING_SAVE_BET,
    BETTING_WITHOUT_PROPS,
    setGameBets,
    updateBetState,
    updateBetValues,
} from './actions';
import { BetState } from './components/BetComponent';
import { bettingGameBetsSelector } from './selectors';

const TENANT: string = getSystemConfig().clientName;

function* bettingLoadGenerator(action: Action<number>) {
    // show the loading indicator
    yield put(bettingSetLoading(true));

    const isLoggedIn: boolean = yield select(isLoggedInSelector);

    try {
        let trId = action.payload;
        if (trId) {
            yield put(bettingSetTrId(String(trId)));
        }

        yield put(
            tournamentSelectionChangeTournamentRound({
                type: 'betting',
                trId: String(trId),
            }),
        );

        if (trId && trId > 0 && isLoggedIn) {
            yield* fetchWrapperFuncNoLoading(() => getBettingApi2().getGameBets({ trId }), [(r) => setGameBets(r)]);
        }

        const tr = yield* fetchWrapperFuncNoLoading(
            () => getTournamentApi2().getTournamentRoundById({ trId }),
            [(r) => bettingSetTournamentRound(r)],
        );
        if (tr) {
            yield* fetchWrapperFuncNoLoading(
                () => getTournamentApi2().getTournamentById({ tId: tr.tournamentId }),
                [(r) => bettingSetTournament(r)],
            );
        }
    } finally {
        yield put(bettingSetLoading(false));
    }
}

function* loadGameInfoGenerator(action: Action<number>) {
    const gameId = action.payload;
    yield* fetchWrapperFuncNoLoading(
        () => getBettingApi2().getGameBetInfo({ tenant: TENANT, gameId }),
        [(r: GameBetInfo) => bettingSetGameInfo(r)],
    );
}

function* bettingWithoutPropsGenerator() {
    yield* loadWithoutPropsTournamentRoundForwarder('betting', bettingPath);
}

function* loadBetQuoteGenerator(action: Action<BetQuoteRequest>) {
    yield* fetchWrapperFuncNoLoading(
        () =>
            getBettingApi2().getBetQuote({
                tenant: TENANT,
                gameId: action.payload.gameId,
                bet1: action.payload.bet1,
                bet2: action.payload.bet2,
                ugId: action.payload.ugId,
            }),
        [(r) => bettingSetBetQuote(r)],
    );
    const gameBets: GameBet[] = yield select(bettingGameBetsSelector);
    const gameBet = gameBets.find((gb) => gb.gameId === action.payload.gameId);
    yield put(bettingShowBetQuote(true));
    if (gameBet) {
        yield put(bettingSetBetQuoteGame(gameBet));
    }
}

function* saveBetGenerator(action: Action<Bet>) {
    let bet = action.payload;
    yield put(updateBetState({ gameId: bet.gameId, betState: BetState.saving }));
    yield put(updateBetValues({ gameId: bet.gameId, bet1: bet.bet1, bet2: bet.bet2 }));

    yield* fetchWrapperFunc(
        () => getBettingApi2().saveGameBets({bet}),
        [() => updateBetState({ gameId: bet.gameId, betState: BetState.saved })],
        () => updateBetState({ gameId: bet.gameId, betState: BetState.error }),
    );
}

function* watchBettingActions() {
    yield takeLatest(BETTING_LOAD, bettingLoadGenerator);
    yield takeEvery(BETTING_SAVE_BET, saveBetGenerator);
    yield takeLatest(BETTING_LOAD_BET_QUOTE, loadBetQuoteGenerator);
    yield takeLatest(BETTING_WITHOUT_PROPS, bettingWithoutPropsGenerator);
    // yield takeLatest(BETTING_SET_GAME_BETS, setGameBetsGenerator);
}

function* watchGameInfos() {
    yield takeEvery(BETTING_LOAD_GAME_INFO, loadGameInfoGenerator);
}

export function* bettingSagas() {
    yield all([watchBettingActions(), watchGameInfos()]);
}
